import { types } from 'mobx-state-tree';
import { isNumber, extend } from 'lodash';
import { randomUUID } from "./uuid";

export const EQFilter = types.model('EQFilter', {
  id: types.identifier(types.string),
  type: 'eq',
  field: types.string,
  value: types.union(
    value => value instanceof Date ? types.Date : (isNumber(value) ? types.number : types.string),
    types.string, types.number, types.Date),

  get query() {
    return {
      [this.field]: this.value
    };
  },
}, {
    setValue(value: string) {
      this.value = value;
    }
  });

export const RegexFilter = types.model('RegexFilter', {
  id: types.identifier(types.string),
  type: 'regex',
  field: types.string,
  value: types.string,
  prefix: types.optional(types.boolean, true),
  caseInsensitive: false,

  get query() {
    return {
      [this.field]: { $regex: this.caseInsensitive ? 
        new RegExp(`${this.prefix ? '^' : ''}${this.value}`, 'i') : this.value }
    };
  }
}, {
    setValue(value: string) {
      this.value = value;
    }
  });

const EqOrRegexFilterMap = types.map(types.union(value => {
  if (value.type === 'eq') {
    return EQFilter;
  } else {
    return RegexFilter;
  }
}, EQFilter, RegexFilter));

export const LessThanFilter = types.model('LessThan', {
  type: 'lt',
  field: types.string,
  value: types.union(
    value => value instanceof Date ? types.Date : (isNumber(value) ? types.number : types.string),
    types.string, types.number, types.Date),
  includeEquals: types.optional(types.boolean, true),

  get query() {
    return {
      [this.field]: this.includeEquals ? { $lte: this.value } : { $lt: this.value }
    };
  }
});

export const GreaterThanFilter = types.model('GreatherThan', {
  id: types.optional(types.identifier(types.string), () => randomUUID()),
  type: 'gt',
  field: types.string,
  value: types.union(
    value => value instanceof Date ? types.Date : (isNumber(value) ? types.number : types.string),
    types.string, types.number, types.Date),
  includeEquals: types.optional(types.boolean, true),

  get query() {
    return {
      [this.field]: this.includeEquals ? { $gte: this.value } : { $gt: this.value }
    };
  }
});

export const InFilter = types.model('InFilter', {
  type: 'in',
  field: types.string,
  value: types.array(types.union(types.string, types.number)),

  get query() {
    return {
      [this.field]: { $in: this.value }
    };
  }
});

export const NotInFilter = types.model('NotInFilter', {
  type: 'nin',
  field: types.string,
  value: types.array(types.union(types.string, types.number)),

  get query() {
    return {
      [this.field]: { $nin: this.value }
    };
  }
});

export const ORFilter = types.model('ORFiler', {
  id: types.identifier(types.string),
  type: 'or',
  filters: EqOrRegexFilterMap,

  get query() {
    return {
      $or: this.filters.values().map(f => f.query)
    };
  }
}, {
    addFilter(filter: typeof EQFilter.Type | typeof RegexFilter.Type) {
      this.filters.put(filter);
    }
  });

export const ANDFilter = types.model('ANDFiler', {
  id: types.identifier(types.string),
  type: 'and',
  filters: types.map(types.union(EQFilter, RegexFilter, ORFilter)), // EqOrRegexFilterMap,

  get query() {
    return {
      $and: this.filters.values().map(f => f.query)
    };
  }
}, {
    addFilter(filter: typeof EQFilter.Type | typeof RegexFilter.Type) {
      this.filters.put(filter);
    }
  });

export const NOFilter = types.model('NoFilter', {
  type: 'noFilter',
  get query() {
    return {};
  }
});

export const Sort = types.model('Sort', {
  fieldName: types.string,
  ascending: types.boolean,

  get query() {
    return {
      $sort: {
        [this.fieldName]: this.ascending ? 1 : -1
      }
    };
  }
});

export const AllFilterType = types.union(value => {
  if (!value) {
    return types.null;
  }
  if (value.type === 'noFilter') {
    return NOFilter;
  }
  if (value.type === 'and') {
    return ANDFilter;
  }
  if (value.type === 'or') {
    return ORFilter;
  }
  if (value.type === 'eq') {
    return EQFilter;
  }
  if (value.type === 'regex') {
    return RegexFilter;
  }
  if (value.type === 'nin') {
    return NotInFilter;
  }
  if (value.type === 'in') {
    return InFilter;
  }
  if (value.type === 'gt') {
    return GreaterThanFilter;
  }
  if (value.type === 'lt') {
    return LessThanFilter;
  }
  throw new Error(`unknown type ${value.type}`);
},
  ANDFilter,
  ORFilter,
  EQFilter,
  RegexFilter,
  NotInFilter,
  InFilter,
  GreaterThanFilter,
  LessThanFilter,
  NOFilter,
  types.null
);

export const Query = types.model('Query', {
  filter: types.maybe(AllFilterType),
  sort: types.maybe(Sort),
  limit: types.maybe(types.number),
  skip: types.maybe(types.number),
  select: types.maybe(types.array(types.string)),

  get query() {
    return extend({},
      this.filter ? this.filter.query : {},
      this.sort ? this.sort.query : {},
      this.limit ? { $limit: this.limit } : {},
      this.skip ? { $skip: this.skip } : {},
      this.select ? { $select: this.select } : {},
    );
  }
}, {
    setFilter(filter: typeof AllFilterType.Type) {
      this.filter = filter;
    },
    addFilter(filter: typeof AllFilterType.Type) {
      if (this.filter) {
        if (this.filter.type === 'and' ||
          this.filter.type === 'or') {
          // tslint:disable-next-line:no-any
          (this.filter as any).addFilter(filter);
        } else {
          this.setFilter(filter);
        }
      }
    },
    removeFilter() {
      this.filter = NOFilter.create();
    },
    setSort(sort: typeof Sort.Type) {
      this.sort = sort;
    }
  });

export type FilterType =
  typeof ANDFilter |
  typeof ORFilter |
  typeof EQFilter |
  typeof RegexFilter |
  typeof NotInFilter |
  typeof InFilter |
  typeof GreaterThanFilter |
  typeof LessThanFilter;


// WEBPACK FOOTER //
// ./~/tslint-loader!./src/utils/filters.ts